import type { ProjectMeta } from '@/services/project-api'
import {
  isOrphanedGenerationMarker,
  readProjectGenerationMarker,
} from '@/features/project/lib/project-generation-durability'
import {
  getProjectProofHealth,
  type ProjectProofHealth,
  type ProjectProofHealthCopySource,
} from '@/features/project/lib/project-proof-health'

export interface HomeContinuityProject {
  project: ProjectMeta
  proofHealth: ProjectProofHealth
  generationOrphaned: boolean
}

export interface HomeContinuityModel {
  recent: HomeContinuityProject[]
  attention: HomeContinuityProject[]
}

/**
 * Derives Home continuity from the already-loaded project list. This is a
 * read-only projection: it never adds a request or alters store-owned data.
 */
export function buildHomeContinuityModel(
  /** Threaded through to `getProjectProofHealth`; see its doc for why the
   *  copy is a required argument rather than an English default. */
  source: ProjectProofHealthCopySource,
  projects: readonly ProjectMeta[],
  now: number,
): HomeContinuityModel {
  const ordered = [...projects]
    .sort((left, right) => updatedAtMs(right) - updatedAtMs(left))
    .map((project) => projectContinuity(source, project, now))

  return {
    recent: ordered.slice(0, 3),
    attention: ordered.filter(
      (item) =>
        item.generationOrphaned ||
        item.proofHealth.status === 'review' ||
        item.proofHealth.status === 'blocked',
    ),
  }
}

function projectContinuity(
  source: ProjectProofHealthCopySource,
  project: ProjectMeta,
  now: number,
): HomeContinuityProject {
  const generation = readProjectGenerationMarker({
    generation: project.generation,
  })

  return {
    project,
    proofHealth: getProjectProofHealth(source, project),
    generationOrphaned: isOrphanedGenerationMarker(generation, now),
  }
}

function updatedAtMs(project: ProjectMeta): number {
  const timestamp = Date.parse(project.updated_at)
  return Number.isFinite(timestamp) ? timestamp : 0
}
